
import { v4 as uuidv4 } from "uuid";
import { ModelBase } from "@models/UpsertableBase";
import { 
  AbilityScores, 
  Bonus, 
  CharacterAbility, 
  CharacterAction, 
  CharacterAdvantage, 
  CharacterASIBaseFeature, 
  CharacterFeature, 
  CharacterFeatureBase, 
  CharacterProficiency, 
  CharacterResource, 
  CharacterRoll, 
  CharacterSense, 
  CharacterSheetInterface, 
  CharacterSlot, 
  CharacterSpell, 
  Creature, 
  Customization, 
  DamageMultiplierSimple, 
  // CharacterLimitation, 
  MinimumRoll, 
  Modifier, 
  RangeBonus, 
  Reroll, 
  SkillAbilityOverrideFeature, 
  // SpellModifier, 
  // TransformUpgradeFeature, 
  UpgradableNumber, 
  CharacterExtraEffects,
  CreatureHP,
  Character,
  AlternateConditionFeature,
  CharacterResourceHolder,
  BasicCreatureInstance,
  CharacterSubscriptionHolder,
  GreaterAdvantage,
  SpellLimitation,
  // FeatureAncestry,
  CharacterAbilityModifier,
  SizeOptions,
  CharacterCondition,
  iCreatureSpell,
  CreatureDetails
} from "@dndModels";
import { Feature, SmartHash } from "@models";
import { AttackType, CreatureType, DamageType, DataType, SizeType } from "@dndModels/Types";
import { SIZE_MAP } from "@dndModels/Constants";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class Overridable {
  // Override means that while the transform is active, 
  // it overrides the property of the character with this one.
  // Copy means that when the transform instance is initiated 
  // it copies the property from the character, 
  // but if something changes with that property it will not be 
  // carried over back to the character.
  // Share is like Copy except that changes to the property also 
  // happen to the character.  This one needs to be handled carefully.
  // Merge copies the property from the character and merges it with
  // this property when the transform instance is initiated.
  override_mode: "Override" | "Copy" | "Merge" | "Inherit" | "Creature" | "Creature Merge" | "Creature if Greater" | "Override if Greater" = "Copy";
  share = false;

  constructor(obj?: any) {
    if (obj) {
      this.override_mode = obj.override_mode ? (obj.override_mode === "Share" ? "Copy" : obj.override_mode) : "Copy";
      this.share = obj.share ? obj.share : (obj.override_mode && obj.override_mode === "Share");
    }
  }

  toDBObj(): any {
    return {
      override_mode: this.override_mode,
      share: this.share
    }
  }

  copy(copyMe: Overridable) {
    // switch(copyMe.override_mode) {
    //   case "Inherit": this.override_mode = "Inherit"; break;
    //   case "Creature": this.override_mode = "Creature"; break;
    //   case "Copy": this.override_mode = "Copy"; break;
    //   case "Merge": this.override_mode = "Merge"; break;
    //   case "Creature Merge": this.override_mode = "Creature Merge"; break;
    //   case "Override": this.override_mode = "Override"; break;
    //   case "Share": this.override_mode = "Share"; break;
    // }
    this.override_mode = copyMe.override_mode;
    this.share = copyMe.share;
  }

  clone() {
    const newObj = new Overridable();
    newObj.copy(this);
    return newObj;
  }
}

export class OverridableString extends Overridable {
  value = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.value = obj.value ? obj.value : "";
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      value: this.value
    }
  }

  copy(copyMe: OverridableString) {
    super.copy(copyMe);
    this.value = copyMe.value;
  }

  clone() {
    const newObj = new OverridableString();
    newObj.copy(this);
    return newObj;
  }
}

export class OverridableStringArray extends Overridable {
  values: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.values = obj.value ? obj.value : [];
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      value: this.values
    }
  }

  copy(copyMe: OverridableStringArray) {
    super.copy(copyMe);
    this.values = copyMe.values;
  }

  clone() {
    const newObj = new OverridableStringArray();
    newObj.copy(this);
    return newObj;
  }
}

export class OverridableNumber extends Overridable {
  value: UpgradableNumber = new UpgradableNumber();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (typeof(obj) === "number") {
        this.value = new UpgradableNumber(obj);
      } else {
        this.value = obj.value ? new UpgradableNumber(obj.value) : new UpgradableNumber();
      }
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      value: this.value.toDBObj()
    }
  }

  copy(copyMe: OverridableNumber) {
    super.copy(copyMe);
    this.value = copyMe.value.clone();
  }

  clone(log_it = false) {
    if (log_it) {
      debugger;
    }
    const newObj = new OverridableNumber();
    newObj.copy(this);
    return newObj;
  }
}

export class OverridableAbilityScores {
  strength: OverridableNumber = new OverridableNumber();
  dexterity: OverridableNumber = new OverridableNumber();
  constitution: OverridableNumber = new OverridableNumber();
  intelligence: OverridableNumber = new OverridableNumber();
  wisdom: OverridableNumber = new OverridableNumber();
  charisma: OverridableNumber = new OverridableNumber();

  constructor(obj?: any) {
    if (obj) {
      if (typeof(obj) === "number") {
        this.strength.override_mode = "Override";
        this.dexterity.override_mode = "Override";
        this.constitution.override_mode = "Override";
        this.intelligence.override_mode = "Override";
        this.wisdom.override_mode = "Override";
        this.charisma.override_mode = "Override";
        this.strength.value = new UpgradableNumber(obj);
        this.dexterity.value = new UpgradableNumber(obj);
        this.constitution.value = new UpgradableNumber(obj);
        this.intelligence.value = new UpgradableNumber(obj);
        this.wisdom.value = new UpgradableNumber(obj);
        this.charisma.value = new UpgradableNumber(obj);
      } else if (obj.override_mode) {
        this.strength.override_mode = obj.override_mode;
        this.dexterity.override_mode = obj.override_mode;
        this.constitution.override_mode = obj.override_mode;
        this.intelligence.override_mode = obj.override_mode;
        this.wisdom.override_mode = obj.override_mode;
        this.charisma.override_mode = obj.override_mode;
        this.strength.value = new UpgradableNumber();
        this.dexterity.value = new UpgradableNumber();
        this.constitution.value = new UpgradableNumber();
        this.intelligence.value = new UpgradableNumber();
        this.wisdom.value = new UpgradableNumber();
        this.charisma.value = new UpgradableNumber();
      } else {
        this.strength = obj.strength ? new OverridableNumber(obj.strength) : new OverridableNumber();
        this.dexterity = obj.dexterity ? new OverridableNumber(obj.dexterity) : new OverridableNumber();
        this.constitution = obj.constitution ? new OverridableNumber(obj.constitution) : new OverridableNumber();
        this.intelligence = obj.intelligence ? new OverridableNumber(obj.intelligence) : new OverridableNumber();
        this.wisdom = obj.wisdom ? new OverridableNumber(obj.wisdom) : new OverridableNumber();
        this.charisma = obj.charisma ? new OverridableNumber(obj.charisma) : new OverridableNumber();
      }
    }
  }

  getAbilityScore(ability: string) {
    let score: OverridableNumber | null = null;
    switch(ability.toLocaleLowerCase().substring(0, 3)) {
      case "str":
        score = this.strength;
      break;
      case "dex":
        score = this.dexterity;
      break;
      case "con":
        score = this.constitution;
      break;
      case "int":
        score = this.intelligence;
      break;
      case "wis":
        score = this.wisdom;
      break;
      case "cha":
        score = this.charisma;
      break;
    }
    return score;
  }

  toDBObj(): any {
    return {
      strength: this.strength.toDBObj(),
      dexterity: this.dexterity.toDBObj(),
      constitution: this.constitution.toDBObj(),
      intelligence: this.intelligence.toDBObj(),
      wisdom: this.wisdom.toDBObj(),
      charisma: this.charisma.toDBObj(),
    }
  }

  copy(copyMe: OverridableAbilityScores) {
    this.strength = copyMe.strength.clone();
    this.dexterity = copyMe.dexterity.clone();
    this.constitution = copyMe.constitution.clone();
    this.intelligence = copyMe.intelligence.clone();
    this.wisdom = copyMe.wisdom.clone();
    this.charisma = copyMe.charisma.clone();
  }

  clone() {
    const newObj = new OverridableAbilityScores();
    newObj.copy(this);
    return newObj;
  }
}

export interface TransformInterface {
  transform_type: string; // = "";

  id: string;
  name: string;
  description: string;
  
  formulae: { [key: string]: UpgradableNumber }; //  = {};

  image_url_overridable: OverridableString; // = new OverridableString();
  size: { use_options: boolean, overridable?: OverridableNumber, options?: number[] }; // = { use_options: false, overridable: new OverridableNumber(3) }; 
  // max_carrying_capacity: OverridableNumber; // = new OverridableNumber();
  
  current_ability_scores: OverridableAbilityScores; // = new OverridableAbilityScores();
  max_ability_scores: OverridableAbilityScores; // = new OverridableAbilityScores(20);
  
  // condition_hash: { [id: string]: OverridableNumber }; // = {};
  // condition_immunities: OverridableStringArray; // = new OverridableStringArray();

  inspiration: OverridableNumber; // = new OverridableNumber();
  advantage: OverridableNumber; // = new OverridableNumber();
  feature_filters: {
    [feature_type: string]: any
  }; // = { "Default": { block_all: true } };

  current_hit_points: OverridableNumber; // = new OverridableNumber();
  temp_hit_points: OverridableNumber; // = new OverridableNumber();
  calc_max_hp: boolean;
  max_hit_points: OverridableNumber; // = new OverridableNumber();
  hit_dice_count: OverridableNumber;
  hit_dice_size: OverridableNumber;


  can_concentrate: boolean; // = true;
  // spell_filter: any; // = { block_all: true };
  spell_limit: SpellLimitation;
  // ability_filter: any = { block_all: true }
  // spell_as_ability_filter: any = { block_all: true };

  resources: { [resource_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  slots: string[]; // = [];

  // languages_understood: boolean; // = false;
  able_to_speak: boolean; // = false;
  
  // skill_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // armor_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // weapon_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // tool_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // special_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // save_proficiencies: { [prof_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Copy" }) };
  // jack_of_all_trades: boolean; // = false;

  // passive_bonuses: {
  //   [passive: string]: OverridableNumber,
  // }; // = { "Default": new OverridableNumber({ override_mode: "Copy" })};

  speed: {
    [type: string]: OverridableNumber
  }; // = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  armor_class: OverridableNumber; // = new OverridableNumber();
  prof_bonus: OverridableNumber;

  senses: { [sense_id: string]: OverridableNumber }; // = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};

  features: Feature[]; // = [];

  copyInterface: (copyMe: TransformInterface, log_it: boolean) => void;

  clone: () => TransformInterface;
}

const copyInterface = (that: TransformInterface, copyMe: TransformInterface, log_it = false) => {
  that.transform_type = copyMe.transform_type;
  
  that.formulae = {};
  const keys = Object.keys(copyMe.formulae);
  for (const key of keys) {
    that.formulae[key] = copyMe.formulae[key].clone();
  }

  that.image_url_overridable = copyMe.image_url_overridable.clone();
  that.size = { use_options: copyMe.size.use_options }; 
  if (copyMe.size.use_options && copyMe.size.options) {
    that.size.options = [...copyMe.size.options];
  } else if (copyMe.size.overridable) {
    that.size.overridable = copyMe.size.overridable.clone();
  }
  
  that.current_ability_scores = copyMe.current_ability_scores.clone();
  that.max_ability_scores = copyMe.max_ability_scores.clone();

  that.inspiration = copyMe.inspiration.clone();
  that.advantage = copyMe.advantage.clone();
  that.feature_filters = {...copyMe.feature_filters};

  that.current_hit_points = copyMe.current_hit_points.clone();
  that.temp_hit_points = copyMe.temp_hit_points.clone();
  that.max_hit_points = copyMe.max_hit_points.clone();
  that.calc_max_hp = copyMe.calc_max_hp;
  that.hit_dice_count = copyMe.hit_dice_count.clone();
  that.hit_dice_size = copyMe.hit_dice_size.clone();

  that.can_concentrate = copyMe.can_concentrate;
  // that.spell_filter = {...copyMe.spell_filter};
  that.spell_limit = copyMe.spell_limit.clone();

  if (log_it) {
    debugger;
  }
  that.slots = [...copyMe.slots];
  that.resources = {};
  if (copyMe.resources) {
    const keys = Object.keys(copyMe.resources);
    for (const key of keys) {
      that.resources[key] = copyMe.resources[key].clone();
    }
  }

  that.able_to_speak = copyMe.able_to_speak;

  that.speed = {};
  if (copyMe.speed) {
    const keys = Object.keys(copyMe.speed);
    for (const key of keys) {
      that.speed[key] = copyMe.speed[key].clone();
    }
  }
    
  that.armor_class = copyMe.armor_class.clone();
  that.prof_bonus = copyMe.prof_bonus.clone();

  that.senses = {};
  if (copyMe.senses) {
    const keys = Object.keys(copyMe.senses);
    for (const key of keys) {
      that.senses[key] = copyMe.senses[key].clone();
    }
  }
  that.features = copyMe.features.map(o => o.clone());
}

export class Transform extends ModelBase implements TransformInterface {
  data_type: DataType = "transform";

  cost = 1;

  transform_type = "";
  
  formulae: { [key: string]: UpgradableNumber } = {};

  image_url_overridable: OverridableString = new OverridableString();
  size: { use_options: boolean, overridable?: OverridableNumber, options?: number[] } = { use_options: false, overridable: new OverridableNumber({ override_mode: "Override", value: 0 }) }; 
  
  current_ability_scores: OverridableAbilityScores = new OverridableAbilityScores();
  max_ability_scores: OverridableAbilityScores = new OverridableAbilityScores(20);

  inspiration: OverridableNumber = new OverridableNumber();
  advantage: OverridableNumber = new OverridableNumber();
  feature_filters: {
    [feature_type: string]: any
  } = {
    "Default": { block_all: true }
  };

  current_hit_points: OverridableNumber = new OverridableNumber();
  temp_hit_points: OverridableNumber = new OverridableNumber();
  calc_max_hp = false; // If true then max_hit_points equals (hit_dice_count / 2) * (hit_dice_size + 1) + hit_dice_count * CON
  max_hit_points: OverridableNumber = new OverridableNumber();
  hit_dice_count: OverridableNumber = new OverridableNumber();
  hit_dice_size: OverridableNumber = new OverridableNumber();

  can_concentrate = true;
  // spell_filter: any = { block_all: true };
  spell_limit: SpellLimitation = new SpellLimitation();

  resources: { [resource_id: string]: OverridableNumber } = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  slots: string[] = [];

  able_to_speak = false;

  passive_bonuses: {
    [passive: string]: OverridableNumber,
  } = { "Default": new OverridableNumber({ override_mode: "Copy" })};

  speed: {
    [type: string]: OverridableNumber
  } = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  armor_class: OverridableNumber = new OverridableNumber();
  prof_bonus: OverridableNumber = new OverridableNumber({ override_mode: "Copy" });

  senses: { [sense_id: string]: OverridableNumber } = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};

  features: Feature[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.transform_type = obj.transform_type ? obj.transform_type : "";
      this.cost = obj.cost ? obj.cost : 1;
      this.formulae = {};
      if (obj.formulae) {
        for (const key of Object.keys(obj.formulae)) {
          this.formulae[key] = new UpgradableNumber(obj.formulae[key]);
        }
      }
      this.image_url_overridable = obj.image_url_overridable ? new OverridableString(obj.image_url_overridable) : new OverridableString();
      this.size = { use_options: false, overridable: new OverridableNumber(3) }; 
      if (obj.size) {
        this.size.use_options = obj.size.use_options;
        if (obj.size.use_options) {
          this.size.options = obj.size.options;
        } else {
          this.size.overridable = new OverridableNumber(obj.size.overridable);
        }
      }
      // this.max_carrying_capacity = obj.max_carrying_capacity ? new OverridableNumber(obj.max_carrying_capacity) : new OverridableNumber();
      this.current_ability_scores = obj.current_ability_scores ? new OverridableAbilityScores(obj.current_ability_scores) : new OverridableAbilityScores();
      this.max_ability_scores = obj.max_ability_scores ? new OverridableAbilityScores(obj.max_ability_scores) : new OverridableAbilityScores(20);
      
      // this.condition_hash = {};
      // if (obj.condition_hash) {
      //   const keys = Object.keys(obj.condition_hash);
      //   for (const key of keys) {
      //     this.condition_hash[key] = new OverridableNumber(obj.condition_hash[key]);
      //   }
      // }
      // this.condition_immunities = obj.condition_immunities ? new OverridableStringArray(obj.condition_immunities) : new OverridableStringArray();

      this.inspiration = obj.inspiration ? new OverridableNumber(obj.inspiration) : new OverridableNumber();
      this.advantage = obj.advantage ? new OverridableNumber(obj.advantage) : new OverridableNumber();
      this.feature_filters = obj.feature_filters ? obj.feature_filters : { "Default": { block_all: true } };
      
      this.current_hit_points = obj.current_hit_points ? new OverridableNumber(obj.current_hit_points) : new OverridableNumber();
      this.temp_hit_points = obj.temp_hit_points ? new OverridableNumber(obj.temp_hit_points) : new OverridableNumber();
      this.calc_max_hp = obj.calc_max_hp ? obj.calc_max_hp : false;
      this.max_hit_points = obj.max_hit_points ? new OverridableNumber(obj.max_hit_points) : new OverridableNumber();
      this.hit_dice_count = obj.hit_dice_count ? new OverridableNumber(obj.hit_dice_count) : new OverridableNumber();
      this.hit_dice_size = obj.hit_dice_size ? new OverridableNumber(obj.hit_dice_size) : new OverridableNumber();

      this.can_concentrate = obj.can_concentrate !== undefined ? obj.can_concentrate : true;
      // this.spell_filter = obj.spell_filter ? obj.spell_filter : { block_all: true };
      this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
      // this.ability_filter = obj.ability_filter ? obj.ability_filter : { block_all: true };
      // this.spell_as_ability_filter = obj.spell_as_ability_filter ? obj.spell_as_ability_filter : { block_all: true };
      
      this.resources = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
      if (obj.resources) {
        const keys = Object.keys(obj.resources);
        for (const key of keys) {
          this.resources[key] = new OverridableNumber(obj.resources[key]);
        }
      }
      this.slots = obj.slots ? obj.slots : [];
      // this.languages_understood = obj.languages_understood ? obj.languages_understood : false;
      this.able_to_speak = obj.able_to_speak ? obj.able_to_speak : false;
      
      this.passive_bonuses = { "Default": new OverridableNumber({ override_mode: "Copy" })};
      if (obj.passive_bonuses) {
        const keys = Object.keys(obj.passive_bonuses);
        for (const key of keys) {
          this.passive_bonuses[key] = new OverridableNumber(obj.passive_bonuses[key]);
        }
      }

      this.speed = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
      if (obj.speed) {
        const keys = Object.keys(obj.speed);
        for (const key of keys) {
          this.speed[key] = new OverridableNumber(obj.speed[key]);
        }
      }
      this.armor_class = obj.armor_class ? new OverridableNumber(obj.armor_class) : new OverridableNumber();
      this.prof_bonus = obj.prof_bonus ? new OverridableNumber(obj.prof_bonus) : new OverridableNumber({ override_mode: "Copy" });

      this.senses = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
      if (obj.senses) {
        const keys = Object.keys(obj.senses);
        for (const key of keys) {
          this.senses[key] = new OverridableNumber(obj.senses[key]);
        }
      }

      this.features = obj.features ? obj.features.map((o: any) => new Feature(o)) : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.transform_type = obj.transform_type ? obj.transform_type : "";
  //     this.cost = obj.cost ? obj.cost : 1;
  //     this.formulae = {};
  //     if (obj.formulae) {
  //       for (const key of Object.keys(obj.formulae)) {
  //         this.formulae[key] = new UpgradableNumber(obj.formulae[key]);
  //       }
  //     }
  //     this.image_url_overridable = obj.image_url_overridable ? new OverridableString(obj.image_url_overridable) : new OverridableString();
  //     this.size = { use_options: false, overridable: new OverridableNumber(3) }; 
  //     if (obj.size) {
  //       this.size.use_options = obj.size.use_options;
  //       if (obj.size.use_options) {
  //         this.size.options = obj.size.options;
  //       } else {
  //         this.size.overridable = new OverridableNumber(obj.size.overridable);
  //       }
  //     }
  //     // this.max_carrying_capacity = obj.max_carrying_capacity ? new OverridableNumber(obj.max_carrying_capacity) : new OverridableNumber();
  //     this.current_ability_scores = obj.current_ability_scores ? new OverridableAbilityScores(obj.current_ability_scores) : new OverridableAbilityScores();
  //     this.max_ability_scores = obj.max_ability_scores ? new OverridableAbilityScores(obj.max_ability_scores) : new OverridableAbilityScores(20);
      
  //     // this.condition_hash = {};
  //     // if (obj.condition_hash) {
  //     //   const keys = Object.keys(obj.condition_hash);
  //     //   for (const key of keys) {
  //     //     this.condition_hash[key] = new OverridableNumber(obj.condition_hash[key]);
  //     //   }
  //     // }
  //     // this.condition_immunities = obj.condition_immunities ? new OverridableStringArray(obj.condition_immunities) : new OverridableStringArray();

  //     this.inspiration = obj.inspiration ? new OverridableNumber(obj.inspiration) : new OverridableNumber();
  //     this.advantage = obj.advantage ? new OverridableNumber(obj.advantage) : new OverridableNumber();
  //     this.feature_filters = obj.feature_filters ? obj.feature_filters : { "Default": { block_all: true } };
      
  //     this.current_hit_points = obj.current_hit_points ? new OverridableNumber(obj.current_hit_points) : new OverridableNumber();
  //     this.temp_hit_points = obj.temp_hit_points ? new OverridableNumber(obj.temp_hit_points) : new OverridableNumber();
  //     this.calc_max_hp = obj.calc_max_hp ? obj.calc_max_hp : false;
  //     this.max_hit_points = obj.max_hit_points ? new OverridableNumber(obj.max_hit_points) : new OverridableNumber();
  //     this.hit_dice_count = obj.hit_dice_count ? new OverridableNumber(obj.hit_dice_count) : new OverridableNumber();
  //     this.hit_dice_size = obj.hit_dice_size ? new OverridableNumber(obj.hit_dice_size) : new OverridableNumber();

  //     this.can_concentrate = obj.can_concentrate !== undefined ? obj.can_concentrate : true;
  //     // this.spell_filter = obj.spell_filter ? obj.spell_filter : { block_all: true };
  //     this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
  //     // this.ability_filter = obj.ability_filter ? obj.ability_filter : { block_all: true };
  //     // this.spell_as_ability_filter = obj.spell_as_ability_filter ? obj.spell_as_ability_filter : { block_all: true };
      
  //     this.resources = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  //     if (obj.resources) {
  //       const keys = Object.keys(obj.resources);
  //       for (const key of keys) {
  //         this.resources[key] = new OverridableNumber(obj.resources[key]);
  //       }
  //     }
  //     this.slots = obj.slots ? obj.slots : [];
  //     // this.languages_understood = obj.languages_understood ? obj.languages_understood : false;
  //     this.able_to_speak = obj.able_to_speak ? obj.able_to_speak : false;
      
  //     this.passive_bonuses = { "Default": new OverridableNumber({ override_mode: "Copy" })};
  //     if (obj.passive_bonuses) {
  //       const keys = Object.keys(obj.passive_bonuses);
  //       for (const key of keys) {
  //         this.passive_bonuses[key] = new OverridableNumber(obj.passive_bonuses[key]);
  //       }
  //     }

  //     this.speed = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  //     if (obj.speed) {
  //       const keys = Object.keys(obj.speed);
  //       for (const key of keys) {
  //         this.speed[key] = new OverridableNumber(obj.speed[key]);
  //       }
  //     }
  //     this.armor_class = obj.armor_class ? new OverridableNumber(obj.armor_class) : new OverridableNumber();
  //     this.prof_bonus = obj.prof_bonus ? new OverridableNumber(obj.prof_bonus) : new OverridableNumber({ override_mode: "Copy" });

  //     this.senses = { "Default": new OverridableNumber({ override_mode: "Override", value: 0 })};
  //     if (obj.senses) {
  //       const keys = Object.keys(obj.senses);
  //       for (const key of keys) {
  //         this.senses[key] = new OverridableNumber(obj.senses[key]);
  //       }
  //     }

  //     this.features = obj.features ? obj.features.map((o: any) => new Feature(o)) : [];
  //   }
  // }

  get the_effect_types(): string[] {
    return this.features.flatMap(o => o.the_effect_types);
  }

  get the_attack_types(): AttackType[] {
    return this.features.flatMap(o => o.the_attack_types);
  }

  get the_utility_details(): string[] {
    return this.features.flatMap(o => o.the_utility_details);
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    const senses: { [sense_id: string]: any } = {};
    for (const key of Object.keys(this.senses)) {
      senses[key] = this.senses[key].toDBObj();
    }
    const speed: { [type: string]: any } = {};
    for (const key of Object.keys(this.speed)) {
      speed[key] = this.speed[key].toDBObj();
    }
    const resources: { [resource_id: string]: any } = {};
    for (const key of Object.keys(this.resources)) {
      resources[key] = this.resources[key].toDBObj();
    }
    const size: any = { use_options: this.size.use_options };
    if (this.size.use_options && this.size.options) {
      size.options = this.size.options;
    } else if (this.size.overridable) {
      size.overridable = this.size.overridable.toDBObj();
    }
    const passive_bonuses: { [type: string]: any } = {};
    for (const key of Object.keys(this.passive_bonuses)) {
      passive_bonuses[key] = this.passive_bonuses[key].toDBObj();
    }
    const formulae: { [key: string]: any } = {};
    for (const key of Object.keys(this.formulae)) {
      formulae[key] = this.formulae[key].toDBObj();
    }
    return {
      ...super_obj,
      transform_type: this.transform_type,
      cost: this.cost,
      formulae,
      image_url_overridable: this.image_url_overridable.toDBObj(),
      size,
      // max_carrying_capacity: this.max_carrying_capacity.toDBObj(),
      current_ability_scores: this.current_ability_scores.toDBObj(),
      max_ability_scores: this.max_ability_scores.toDBObj(),
      // condition_hash,
      // condition_immunities: this.condition_immunities.toDBObj(),
      inspiration: this.inspiration.toDBObj(),
      advantage: this.advantage.toDBObj(),
      feature_filters: this.feature_filters,
      current_hit_points: this.current_hit_points.toDBObj(),
      temp_hit_points: this.temp_hit_points.toDBObj(),
      calc_max_hp: this.calc_max_hp,
      max_hit_points: this.max_hit_points.toDBObj(),
      hit_dice_count: this.hit_dice_count.toDBObj(),
      hit_dice_size: this.hit_dice_size.toDBObj(),
      can_concentrate: this.can_concentrate,
      // spell_filter: this.spell_filter,
      spell_limit: this.spell_limit.toDBObj(),
      // ability_filter: this.ability_filter,
      // spell_as_ability_filter: this.spell_as_ability_filter,
      slots: this.slots,
      resources,
      // languages_understood: this.languages_understood,
      able_to_speak: this.able_to_speak,
      // skill_proficiencies,
      // armor_proficiencies,
      // weapon_proficiencies,
      // tool_proficiencies,
      // special_proficiencies,
      // save_proficiencies,
      // jack_of_all_trades: this.jack_of_all_trades,
      // passive_bonuses,
      speed,
      armor_class: this.armor_class.toDBObj(),
      prof_bonus: this.prof_bonus.toDBObj(),
      senses,
      features: this.features.map(o => o.toDBObj())
    };
  }

  clone(drop_ids = false): Transform {
    const clone = new Transform();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: Transform, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.cost = copyMe.cost;
    this.copyInterface(copyMe, false);
  }

  copyInterface(copyMe: TransformInterface, log_it: boolean) {
    copyInterface(this, copyMe, log_it);
  }
}

export interface CreaturePotence {
  true_id: string; // = "";
  level: number; // = 1;
  specific_id: string[]; // = [];
  creature_type: string[]; // = []; // beast, fey, etc
  max_cr: UpgradableNumber; // = 0;
  size: string[]; // = [];
  speed: string[]; // = [];
  age_category?: string;
  filter_field_from_extra?: string;
}

/**
 * This is a transform that allows 
 * transforming into a modified version of a creature,
 * like classic Wild Shape, Polymorph, and True Polymorph.
 * 
 * Later I'll add a flag that makes it so they have to learn creatures before being 
 * able to use them.
 */
export class CreatureTransform extends Transform {
  potence_type = "None"; // Character, Class, Slot (Level), or None
  potence_class = "";
  
  creature_potences: CreaturePotence[] = [];
  
  size: { use_options: boolean, overridable?: OverridableNumber, options?: number[] } = { use_options: false, overridable: new OverridableNumber({ override_mode: "Creature" }) }; 
  
  current_ability_scores: OverridableAbilityScores = new OverridableAbilityScores({ override_mode: "Creature" });
  max_ability_scores: OverridableAbilityScores = new OverridableAbilityScores({ override_mode: "Creature" });
  
  inspiration: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  advantage: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  feature_filters: {
    [feature_type: string]: any
  } = {
    "Default": { block_all: true }
  };

  current_hit_points: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  temp_hit_points: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  max_hit_points: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  hit_dice_count: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  hit_dice_size: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });

  can_concentrate = false;
  // spell_filter: any = { block_all: true };
  spell_limit: SpellLimitation = new SpellLimitation();

  resources: { [resource_id: string]: OverridableNumber } = { "Default": new OverridableNumber({ override_mode: "Creature" })};
  slots: string[] = ["Creature"];

  able_to_speak = false;
  
  speed: {
    [type: string]: OverridableNumber
  } = { "Default": new OverridableNumber({ override_mode: "Creature" })};
  armor_class: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });
  prof_bonus: OverridableNumber = new OverridableNumber({ override_mode: "Creature" });

  senses: { [sense_id: string]: OverridableNumber } = { "Default": new OverridableNumber({ override_mode: "Creature" })};

  knowledge_usage_key = "";
  knowledge_type: "Creature" | "Transform" | "Adaptation" | "None" = "None";
  obj_filter: any = {}; // These are the only ones that are learnable sans KnowledgeFeature.
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.potence_type = obj.potence_type ? obj.potence_type : "None";
      this.potence_class = obj.potence_class ? obj.potence_class : "";
      this.creature_potences = [];
      if (obj.creature_potences) {
        for (const cp of obj.creature_potences) {
          this.creature_potences.push({
            true_id: cp.true_id,
            level: cp.level,
            specific_id: cp.specific_id,
            creature_type: cp.creature_type, 
            max_cr: new UpgradableNumber(cp.max_cr),
            size: cp.size,
            speed: cp.speed,
            age_category: cp.age_category,
            filter_field_from_extra: cp.filter_field_from_extra
          });
        }
      }
      
      this.size = { use_options: false, overridable: new OverridableNumber({ override_mode: "Creature" }) }; 
      if (obj.size) {
        this.size.use_options = obj.size.use_options;
        if (obj.size.use_options) {
          this.size.options = obj.size.options;
        } else {
          this.size.overridable = new OverridableNumber(obj.size.overridable);
        }
      }
      this.current_ability_scores = obj.current_ability_scores ? new OverridableAbilityScores(obj.current_ability_scores) : new OverridableAbilityScores({ override_mode: "Creature" });
      this.max_ability_scores = obj.max_ability_scores ? new OverridableAbilityScores(obj.max_ability_scores) : new OverridableAbilityScores({ override_mode: "Creature" });
      
      this.inspiration = obj.inspiration ? new OverridableNumber(obj.inspiration) : new OverridableNumber({ override_mode: "Creature" });
      this.advantage = obj.advantage ? new OverridableNumber(obj.advantage) : new OverridableNumber({ override_mode: "Creature" });
      this.feature_filters = obj.feature_filters ? obj.feature_filters : { "Default": { block_all: true } };
      
      this.current_hit_points = obj.current_hit_points ? new OverridableNumber(obj.current_hit_points) : new OverridableNumber({ override_mode: "Creature" });
      this.temp_hit_points = obj.temp_hit_points ? new OverridableNumber(obj.temp_hit_points) : new OverridableNumber({ override_mode: "Creature" });
      this.max_hit_points = obj.max_hit_points ? new OverridableNumber(obj.max_hit_points) : new OverridableNumber({ override_mode: "Creature" });
      this.calc_max_hp = obj.calc_max_hp ? obj.calc_max_hp : false;
      this.hit_dice_count = obj.hit_dice_count ? new OverridableNumber(obj.hit_dice_count) : new OverridableNumber({ override_mode: "Creature" });
      this.hit_dice_size = obj.hit_dice_size ? new OverridableNumber(obj.hit_dice_size) : new OverridableNumber({ override_mode: "Creature" });

      this.can_concentrate = obj.can_concentrate !== undefined ? obj.can_concentrate : true;
      // this.spell_filter = obj.spell_filter ? obj.spell_filter : { block_all: true };
      this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
      
      this.resources = { "Default": new OverridableNumber({ override_mode: "Creature" })};
      if (obj.resources) {
        const keys = Object.keys(obj.resources);
        for (const key of keys) {
          this.resources[key] = new OverridableNumber(obj.resources[key]);
        }
      }
      this.slots = obj.slots ? obj.slots : ["Creature"];
      this.able_to_speak = obj.able_to_speak ? obj.able_to_speak : false;

      this.speed = { "Default": new OverridableNumber({ override_mode: "Creature" })};
      if (obj.speed) {
        const keys = Object.keys(obj.speed);
        for (const key of keys) {
          this.speed[key] = new OverridableNumber(obj.speed[key]);
        }
      }
      this.armor_class = obj.armor_class ? new OverridableNumber(obj.armor_class) : new OverridableNumber({ override_mode: "Creature" });
      this.prof_bonus = obj.prof_bonus ? new OverridableNumber(obj.prof_bonus) : new OverridableNumber({ override_mode: "Creature" });

      this.senses = { "Default": new OverridableNumber({ override_mode: "Creature" })};
      if (obj.senses) {
        const keys = Object.keys(obj.senses);
        for (const key of keys) {
          this.senses[key] = new OverridableNumber(obj.senses[key]);
        }
      }
      this.knowledge_usage_key = obj.knowledge_usage_key ? obj.knowledge_usage_key : "";
      this.knowledge_type = obj.knowledge_type ? obj.knowledge_type : "None";
      this.obj_filter = obj.obj_filter ? obj.obj_filter : {};
    }
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      potence_type: this.potence_type,
      potence_class: this.potence_class,
      creature_potences: this.creature_potences.map(o => { 
        return { 
          true_id: o.true_id,
          level: o.level,
          specific_id: o.specific_id,
          creature_type: o.creature_type, 
          max_cr: o.max_cr.toDBObj(),
          size: o.size,
          speed: o.speed,
          age_category: o.age_category,
          filter_field_from_extra: o.filter_field_from_extra
        };
      }),
      knowledge_usage_key: this.knowledge_usage_key,
      knowledge_type: this.knowledge_type,
      obj_filter: this.obj_filter,
    };
  }

  clone(drop_ids = false): CreatureTransform {
    const clone = new CreatureTransform();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CreatureTransform, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.potence_type = copyMe.potence_type;
    this.potence_class = copyMe.potence_class;
    this.creature_potences = copyMe.creature_potences.map(o => { return {...o}; });
    this.knowledge_usage_key = copyMe.knowledge_usage_key;
    this.knowledge_type = copyMe.knowledge_type;
    this.obj_filter = {...copyMe.obj_filter};
  }

  copyTransform(copyMe: Transform, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
  }
}

export class TransformInstance implements CharacterSheetInterface, CreatureDetails {
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};

  true_id = "";
  character?: Character;
  transform_id = "";
  transform?: Transform;
  creature_id = "";
  creature?: Creature;
  creature_instance?: BasicCreatureInstance;
  made_with_slot_level = -1;

  // adaptations: AdaptationInstance[] = [];

  image_url = "";

  size: SizeType = 0; 
  base_size: SizeType = 0;
  size_modifier = 0;
  overriding_size: SizeType = 0;
  size_option_features: SizeOptions[] = [];
  size_options: SizeType[] = [];
  size_bonus_for_carrying = 0;
  carrying_capacity_mult = 1;
  max_carrying_capacity = 0;
  connected = false;

  get use_size() {
    if (this.overriding_size === 0) {
      const use_size = this.base_size + this.size_modifier;
      if (SIZE_MAP[use_size]) {
        return use_size as SizeType;
      } else {
        return this.base_size;
      }
    } else {
      return this.overriding_size;
    }
  }

  get size_num() {
    return this.use_size;
  }

  get age_category() {
    if (this.creature_instance) {
      return this.creature_instance.age_category;
    } else if (this.creature) {
      return this.creature.age_category;
    }
    return "";
  }

  get tags() {
    const tags = this.transform?.tags??[];
    if (this.creature_instance) {
      tags.push(...this.creature_instance.tags);
    }
    if (this.creature) {
      tags.push(...this.creature.tags);
    }
    return common_util.unique_array(tags);
  }
  
  current_ability_scores: AbilityScores = new AbilityScores();
  // These limit the effectiveness of any modifiers.  
  // Almost always will be 20 for everything, 
  // but some features change them.
  max_ability_scores: AbilityScores = new AbilityScores(20);
  // This holds all the modifications from ASIs, etc. 
  // so it can easily be displayed
  bonus_ability_score_modifiers: AbilityScores = new AbilityScores(0);
  // This holds the overridden ability scores from things 
  // like Belt of Giant Strength, or Amulet of Health.
  override_ability_scores: AbilityScores = new AbilityScores(null);

  advantage = 0;
  greater_advantages: GreaterAdvantage[] = [];
  inspiration = false;

  resource_holder: CharacterResourceHolder = new CharacterResourceHolder();

  extra_hp_pools: CreatureHP[] = [];

  spells: CharacterSpell[] = [];
  ritual_only: CharacterSpell[] = [];
  abilities: CharacterAbility[] = [];
  spell_as_abilities: CharacterAbility[] = [];
  
  get hit_dice() {
    return this.resource_holder.hit_dice;
  }
  get resources() {
    return this.resource_holder.resources;
  }
  get slots() {
    return this.resource_holder.slots;
  }

  get use_slots(): CharacterSlot[] {
    return this.slots;
  }
  get infusions_by_level() {
    return this.resource_holder.infusions;
  }
  get infusions() {
    return this.infusions_by_level.flatMap(o => o.infusions);
  }

  get use_slots2(): {
    resource_id: string, // Can be "Slot:Level" or "Pact:Level"
    used: number,
    total: number
  }[] {
    return this.slots.map(o => {
      const s: {
        resource_id: string, // Can be "Slot:Level" or "Pact:Level"
        used: number,
        total: number
      } = {
        resource_id: `${o.slot_name}:${o.level}`,
        used: o.used,
        total: o.total
      };
      return s;
    });
  }

  get use_resources(): {
    resource_id: string, // Can be "Slot:Level" or "Pact:Level"
    used: number,
    total: number,
    size: number
  }[] {
    return this.resources.map(o => {
      const r: {
        resource_id: string, // Can be "Slot:Level" or "Pact:Level"
        used: number,
        total: number,
        size: number
      } = {
        resource_id: o.type_id, 
        used: o.used,
        total: o.total,
        size: o.size
      };
      return r;
    });
  }

  get use_spells() {
    const spells: iCreatureSpell[] = [];
    for (const spell of this.spells) {
      spells.push({ spell_level: spell.level, spell_id: spell.spell_id, cast_type: "Slot", x: 0 });
    }
    for (const spell of this.spell_as_abilities) {
      let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "Slot";
      if (spell.at_will) {
        cast_type = "At Will";
      } else if (spell.ritual_only) {
        cast_type = "Ritual";
      }
      spells.push({ spell_level: spell.level, spell_id: spell.spell_id, cast_type, x: 0 });
    }
    return spells;
  }

  favorite_conditions: string[] = [];
  // condition_hash: { [id: string]: { level: number, source_true_id: string } } = {};
  condition_immunities: string[] = [];
  conditions: CharacterCondition[] = [];

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }
  
  languages_known: string[] = [];
  
  proficiencies: CharacterProficiency[] = [];
  jack_of_all_trades = false;
  
  table_modifiers: Modifier[] = [];
  spell_modifiers: CharacterAbilityModifier[] = [];
  passive_bonuses: {
    perception: number,
    insight: number,
    investigation: number
  } = {
    perception: 0,
    insight: 0,
    investigation: 0
  };
  range_bonuses: RangeBonus[] = [];

  rerolls: Reroll[] = [];
  minimum_rolls: MinimumRoll[] = [];
  
  speed: {
    walk: number,
    swim: number,
    climb: number,
    fly: number,
    burrow: number
  } = {
    walk: 0,
    swim: 0,
    climb: 0,
    fly: 0,
    burrow: 0
  };

  get flying() {
    return this.speed.fly > 0;
  }

  get swimming() {
    return this.speed.swim > 0;
  }
  
  attack_bonuses: Bonus[] = [];
  damage_bonuses: Bonus[] = [];
  check_bonuses: Bonus[] = [];
  initiative_modifier = 0;
  initiative_bonuses: Bonus[] = [];
  base_armor_class = 10;
  armor_class = 10;
  max_ac_dex_bonus_for_med_armor = 2;
  ability_checks: { 
    STR: CharacterRoll, 
    DEX: CharacterRoll, 
    CON: CharacterRoll, 
    INT: CharacterRoll, 
    WIS: CharacterRoll, 
    CHA: CharacterRoll 
  } = { 
    STR: new CharacterRoll(), 
    DEX: new CharacterRoll(), 
    CON: new CharacterRoll(), 
    INT: new CharacterRoll(), 
    WIS: new CharacterRoll(), 
    CHA: new CharacterRoll()
  };
  initiative_check: CharacterRoll = new CharacterRoll();
  saving_throw_bonuses: Bonus[] = [];
  saving_throw_profs: { [ability_score: string]: number } = {};
  saving_throws: { 
    [ability_score: string]: CharacterRoll
  } = { 
    STR: new CharacterRoll(), 
    DEX: new CharacterRoll(), 
    CON: new CharacterRoll(), 
    INT: new CharacterRoll(), 
    WIS: new CharacterRoll(), 
    CHA: new CharacterRoll()
  };
  
  AC_customization: Customization = { AC: {} };
  speed_customizations: Customization = {};
  sense_customizations: Customization = {};
  display_speed: "walk" | "swim" | "climb" | "fly" | "burrow" = "walk";
  saving_throw_customizations: Customization = {};
  skill_customizations: Customization = {};
  max_ability_score_customizations: Customization = {};
  ability_score_customizations: Customization = {};
  non_skill_prof_customizations: { [prof_type: string]: { id: string, proficiency: boolean | null, notes: string }[] } = {};
  
  skill_checks: { skill_id: string, skill_check: CharacterRoll }[] = [];

  senses: CharacterSense[] = [];
  
  advantages: CharacterAdvantage[] = [];
  damage_multipliers: DamageMultiplierSimple[] = [];
  extra_attacks = 0;
  unarmed_strike_scores: string[] = [];
  unarmed_strike_bonus_action = false;
  unarmed_strike_damage_types: DamageType[] = [];
  unarmed_strike_size = 0;
  unarmed_strike_count = 0;
  unarmed_strike_add_attack_to_dmg = false;
  show_extra_conditions: string[] = [];
  
  actions: CharacterAction[] = [];

  proficiency_modifier = 0;

  character_level = 0;

  override_creature_type?: CreatureType;
  creature_type: CreatureType = "None";

  char_feature_bases: CharacterFeatureBase[] = [];

  features: CharacterFeature[] = [];
  resource_features: CharacterFeature[] = [];
  ability_features: CharacterFeature[] = [];
  characteristic_features: CharacterFeature[] = [];
  asi_features: CharacterFeature[] = [];
  ability_score_features: { cf: CharacterFeature, casibf: CharacterASIBaseFeature }[] = [];
  
  language_features: CharacterFeature[] = [];
  
  proficiency_features: CharacterFeature[] = [];

  modifier_features: CharacterFeature[] = [];
  spell_modifier_features: CharacterFeature[] = [];
  disable_action_features: CharacterFeature[] = [];
  attack_upgrades: CharacterFeature[] = [];
  weapon_damage_upgrades: CharacterFeature[] = [];
  extra_effects: CharacterExtraEffects[] = [];
  extra_effects_features: CharacterFeature[] = [];
  // bonus_action_attacks: CharacterFeature[] = [];
  light_attack_add_damage_mods: CharacterFeature[] = [];

  source_features: CharacterFeature[] = [];

  sense_features: CharacterFeature[] = [];

  start_equipment_features: CharacterFeature[] = [];
  misc_features: CharacterFeature[] = [];
  spellcasting_features: CharacterFeature[] = [];
  alternate_condition_features: AlternateConditionFeature[] = [];
  ignore_stealth_disadvantage_for_armor_type_ids: string[] = [];

  transform_upgrades: CharacterFeature[] = [];

  // knowledge_features: CharacterFeature[] = [];
  
  formula_values: { [key: string]: number } = {};
  skill_ability_overrides: SkillAbilityOverrideFeature[] = [];

  _name = "";
  
  get cgift_ids(): string[] {
    return [];
  }

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this._name = obj._name ? obj._name : "";
      this.transform_id = obj.transform_id ? obj.transform_id : "";
      this.made_with_slot_level = obj.made_with_slot_level ? obj.made_with_slot_level : -1;
      this.creature_id = obj.creature_id ? obj.creature_id : "";
      // this.adaptations = [];
      // if (obj.adaptations && obj.adaptations.length > 0) {
      //   for (const adaptation of obj.adaptations) {
      //     this.adaptations.push(new AdaptationInstance(adaptation));
      //   }
      // }
      this.image_url = obj.image_url ? obj.image_url : "";
      this.base_size = obj.base_size ? obj.base_size : 0;
      this.size_modifier = obj.size_modifier ? obj.size_modifier : 0;
      this.overriding_size = obj.overriding_size ? obj.overriding_size : 0;
      this.speed_customizations = obj.speed_customizations ? obj.speed_customizations : {
        walk: { value: -1, notes: "" },
        swim: { value: -1, notes: "" },
        climb: { value: -1, notes: "" },
        fly: { value: -1, notes: "" },
        burrow: { value: -1, notes: "" }
      };
      this.non_skill_prof_customizations = obj.non_skill_prof_customizations ? obj.non_skill_prof_customizations : {};
      this.saving_throw_customizations = obj.saving_throw_customizations ? obj.saving_throw_customizations : {};
      this.skill_customizations = obj.skill_customizations ? obj.skill_customizations : {};
      this.max_ability_score_customizations = obj.max_ability_score_customizations ? obj.max_ability_score_customizations : {};
      this.ability_score_customizations = obj.ability_score_customizations ? obj.ability_score_customizations : {};
      
      this.AC_customization = obj.AC_customization ? obj.AC_customization : { AC: {} };
      this.display_speed = obj.display_speed ? obj.display_speed : "walk";
      
      if (obj.resource_holder) {
        this.resource_holder = new CharacterResourceHolder(obj.resource_holder);
      } else {
        this.resource_holder = new CharacterResourceHolder();
        this.resource_holder.hp = obj.hp ? new CreatureHP(obj.hp) : new CreatureHP();
        if (obj.resources && obj.resources.length > 0) {
          for (let i = 0; i < obj.resources.length; i++) {
            const char_resource = new CharacterResource(obj.resources[i]);
            this.resource_holder.resources.push(char_resource);
          }
        }
      }

      // this.advantage = obj.advantage ? obj.advantage : null;
      
      // These are populated and are not stored separately from the character and the transform
      this.inspiration = false; // obj.inspiration ? obj.inspiration : 0;
      this.actions = [];
      this.extra_attacks = 0;
      this.advantages = [];
      this.damage_multipliers = [];

      this.senses = [];
      
      this.current_ability_scores = obj.current_ability_scores ? new AbilityScores(obj.current_ability_scores) : new AbilityScores();
      this.languages_known = []; // obj.languages_known ? obj.languages_known : [];

      this.proficiencies = []; // obj.proficiencies ? obj.proficiencies : [];
      
      this.speed = {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0
      };

      this.initiative_modifier = 0;
      this.initiative_bonuses = [];
      this.saving_throw_profs = {
        STR: 0, DEX: 0, CON: 0, 
        INT: 0, WIS: 0, CHA: 0
      };
      this.max_ability_scores = obj.max_ability_scores ? new AbilityScores(obj.max_ability_scores) : new AbilityScores();
      this.spells = [];
      // if (obj.spells && obj.spells.length > 0) {
      //   for (let i = 0; i < obj.spells.length; i++) {
      //     const char_spell = new CharacterSpell(obj.spells[i]);
      //     this.spells.push(char_spell);
      //   }
      // }
      this.abilities = [];
      // if (obj && obj.abilities && obj.abilities.length > 0) {
      //   for (let i = 0; i < obj.abilities.length; i++) {
      //     const char_ability = new CharacterAbility(obj.abilities[i]);
      //     this.abilities.push(char_ability);
      //   }
      // }
      this.spell_as_abilities = [];
      // if (obj && obj.spell_as_abilities && obj.spell_as_abilities.length > 0) {
      //   for (let i = 0; i < obj.spell_as_abilities.length; i++) {
      //     const char_ability = new CharacterAbility(obj.spell_as_abilities[i]);
      //     this.spell_as_abilities.push(char_ability);
      //   }
      // }
      this.ritual_only = [];
      // if (obj.ritual_only && obj.ritual_only.length > 0) {
      //   for (let i = 0; i < obj.ritual_only.length; i++) {
      //     const char_spell = new CharacterSpell(obj.ritual_only[i]);
      //     this.ritual_only.push(char_spell);
      //   }
      // }
      
      // debugger;
      
      // I'll implement these later if/when I see the need
      this.attack_bonuses = [];
      this.damage_bonuses = [];
      this.saving_throw_bonuses = [];
      this.check_bonuses = [];
      this.unarmed_strike_scores = ["STR"];
      this.unarmed_strike_bonus_action = false;
      this.unarmed_strike_damage_types = ["Bludgeoning"];
      this.unarmed_strike_size = 4;
      this.unarmed_strike_count = 1;
      this.rerolls = [];
      this.table_modifiers = [];
    }
  }
  get swarm() { return this.creature?.swarm??false; }
  get min_challenge_rating() { return this.challenge_rating; }
  get max_challenge_rating() { return this.challenge_rating; }
  get _id() { return this.creature?._id??""; }

  get challenge_rating() {
    return this.creature_instance ? this.creature_instance.challenge_rating : this.character_level;
  }
  get use_challenge_rating() {
    return this.challenge_rating;
  }

  get skill_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Skill");
  }

  get tool_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Tool");
  }

  get hp() {
    return this.resource_holder.hp;
  }

  get true_max_hit_points() {
    return this.hp.true_max;
  }

  get special_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Special");
  }

  get weapon_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Weapon");
  }

  get armor_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Armor");
  }

  get saving_throw_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Saving Throw");
  }

  get expertises() {
    return this.proficiencies.filter(o => o.type === "Expertise");
  }

  get name() {
    if (this._name === "") {
      if (this.transform) {
        if (this.creature_instance) {
          return `${this.transform.name} - ${this.creature_instance.name}`;
        } else {
          return this.transform.name;
        }
      }
    }
    return this._name;
  }

  set name(value: string) {
    this._name = value;
  }

  get subtypes() {
    return this.creature_instance?.subtypes??[];
  }

  get alignment() {
    return this.creature_instance?.alignment??"";
  }

  get speed_string(): string {
    return this.creature_instance ? this.creature_instance.speed_string : "";
  }

  get skills_string(): string {
    return this.creature_instance ? this.creature_instance.skills_string : "";
  }

  get tools_string(): string {
    return this.creature_instance ? this.creature_instance.tools_string : "";
  }

  get immunities_string(): string {
    return this.creature_instance ? this.creature_instance.immunities_string : "";
  }

  get resistances_string(): string {
    return this.creature_instance ? this.creature_instance.resistances_string : "";
  }

  get vulnerabilities_string(): string {
    return this.creature_instance ? this.creature_instance.vulnerabilities_string : "";
  }

  get senses_string(): string {
    const response = "";
    return response;
  }

  get languages() {
    return this.creature_instance ? this.creature_instance.languages : "";
  }

  get traits(): CharacterFeature[] {
    return [];
  }

  get legendary_actions(): CharacterFeature[] {
    return [];
  }

  get description() {
    return this.creature_instance?.description??"";
  }

  get_saving_throw(ability: string) {
    const modifier = this.saving_throws[ability];
    if (!modifier) {
      return 0
    } else {
      return modifier.modifier;
    }
  }
  
  get spellcasting_ability() {
    return this.creature_instance ? this.creature_instance.spellcasting_ability : "";
  }

  get spell_dc() {
    return this.creature_instance ? this.creature_instance.spell_dc : new UpgradableNumber();
  }

  get spell_attack() {
    return this.creature_instance ? this.creature_instance.spell_attack : new UpgradableNumber();
  }

  get non_magic_attack() {
    return this.creature_instance ? this.creature_instance.non_magic_attack : new UpgradableNumber();
  }

  get magic_dmg_bonus() {
    return this.creature_instance ? this.creature_instance.magic_dmg_bonus : new UpgradableNumber();
  }

  get non_magic_dmg_bonus() {
    return this.creature_instance ? this.creature_instance.non_magic_dmg_bonus : new UpgradableNumber();
  }

  get spellcasting_description(): string {
    return this.creature_instance ? this.creature_instance.spellcasting_description : "";
  }

  get string_hash() {
    return this.creature_instance ? this.creature_instance.string_hash : {};
  }

  get upgradable_hash() {
    return this.creature_instance ? this.creature_instance.upgradable_hash : {};
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      transform_id: this.transform_id,
      made_with_slot_level: this.made_with_slot_level,
      creature_id: this.creature_id,
      // adaptations: this.adaptations.map(o => o.toDBObj()),
      image_url: this.image_url,
      base_size: this.base_size,
      size_modifier: this.size_modifier,
      overriding_size: this.overriding_size,
      speed_customizations: this.speed_customizations,
      sense_customizations: this.sense_customizations,
      saving_throw_customizations: this.saving_throw_customizations,
      skill_customizations: this.skill_customizations,
      current_ability_scores: this.current_ability_scores.toDBObj(),
      max_ability_scores: this.max_ability_scores.toDBObj(),
      max_ability_score_customizations: this.max_ability_score_customizations,
      ability_score_customizations: this.ability_score_customizations,
      display_speed: this.display_speed,
      AC_customization: this.AC_customization,
      non_skill_prof_customizations: this.non_skill_prof_customizations,
      resource_holder: this.resource_holder.toDBObj(),
      advantage: this.advantage,
    };
  }

  clone(): TransformInstance {
    const clone = new TransformInstance();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: TransformInstance): void {
    this.true_id = copyMe.true_id;
    this.transform_id = copyMe.transform_id;
    this.made_with_slot_level = copyMe.made_with_slot_level;
    this.creature_id = copyMe.creature_id;
    // this.adaptations = copyMe.adaptations.map(o => o.clone());
    this.image_url = copyMe.image_url;

    this.size = copyMe.size; 
    this.base_size = copyMe.base_size;
    this.size_modifier = copyMe.size_modifier;
    this.overriding_size = copyMe.overriding_size;
    this.size_bonus_for_carrying = copyMe.size_bonus_for_carrying;
    this.carrying_capacity_mult = copyMe.carrying_capacity_mult;
    this.max_carrying_capacity = copyMe.max_carrying_capacity;
    
    this.current_ability_scores = copyMe.current_ability_scores.clone();
    this.bonus_ability_score_modifiers = copyMe.bonus_ability_score_modifiers.clone();
    this.override_ability_scores = copyMe.override_ability_scores.clone();
    this.max_ability_scores = copyMe.max_ability_scores.clone();

    this.advantage = copyMe.advantage;
    this.inspiration = copyMe.inspiration;

    // this.override_max_hit_points = copyMe.override_max_hit_points;
    // this.max_hit_points_modifier = copyMe.max_hit_points_modifier;
    // this.current_hit_points = copyMe.current_hit_points;
    // this.temp_hit_points = copyMe.temp_hit_points;
    this.resource_holder.hp = copyMe.hp.clone();
    
    this.speed_customizations = {...copyMe.speed_customizations};
    this.sense_customizations = {...copyMe.sense_customizations};
    this.saving_throw_customizations = {...copyMe.saving_throw_customizations};
    this.skill_customizations = {...copyMe.skill_customizations};
    this.max_ability_score_customizations = {...copyMe.max_ability_score_customizations};
    this.ability_score_customizations = {...copyMe.ability_score_customizations};

    this.display_speed = copyMe.display_speed;
    this.AC_customization = {...copyMe.AC_customization};
    this.non_skill_prof_customizations = {...copyMe.non_skill_prof_customizations};

    this.spells = copyMe.spells.map(o => o.clone());
    this.ritual_only = copyMe.ritual_only.map(o => o.clone());
    // this.special_spells = copyMe.special_spells.map(o => o.clone());
    this.abilities = copyMe.abilities.map(o => o.clone());
    this.spell_as_abilities = copyMe.spell_as_abilities.map(o => o.clone());

    // Calculated attributes
    // this.max_hit_points = copyMe.max_hit_points;

    this.languages_known = [...copyMe.languages_known];
    
    this.proficiencies = copyMe.proficiencies.map(o => { return {...o}; });
    this.jack_of_all_trades = copyMe.jack_of_all_trades;

    this.table_modifiers = [...copyMe.table_modifiers]; // same
    this.spell_modifiers = copyMe.spell_modifiers.map(o => o.clone());
    this.passive_bonuses = {...copyMe.passive_bonuses};
    this.range_bonuses = copyMe.range_bonuses.map(o => { return {...o}; });

    this.rerolls = copyMe.rerolls.map(o => o.clone());
    this.minimum_rolls = copyMe.minimum_rolls.map(o => o.clone());

    this.speed = {
      walk: copyMe.speed.walk,
      swim: copyMe.speed.swim,
      climb: copyMe.speed.climb,
      fly: copyMe.speed.fly,
      burrow: copyMe.speed.burrow
    };

    this.attack_bonuses = copyMe.attack_bonuses.map(o => o.clone());
    this.damage_bonuses = copyMe.damage_bonuses.map(o => o.clone());
    this.saving_throw_bonuses = copyMe.saving_throw_bonuses.map(o => o.clone());
    this.check_bonuses = copyMe.check_bonuses.map(o => o.clone());
    this.initiative_modifier = copyMe.initiative_modifier;
    this.initiative_bonuses = copyMe.initiative_bonuses.map(o => o.clone());
    this.base_armor_class = copyMe.base_armor_class;
    this.armor_class = copyMe.armor_class;
    this.max_ac_dex_bonus_for_med_armor = copyMe.max_ac_dex_bonus_for_med_armor;
    this.saving_throw_profs = {...copyMe.saving_throw_profs};

    this.ability_checks = { 
      STR: copyMe.ability_checks.STR.clone(), 
      DEX: copyMe.ability_checks.STR.clone(),
      CON: copyMe.ability_checks.STR.clone(),
      INT: copyMe.ability_checks.STR.clone(),
      WIS: copyMe.ability_checks.STR.clone(),
      CHA: copyMe.ability_checks.STR.clone(),
    };
    this.initiative_check = copyMe.initiative_check.clone();
    this.saving_throws = { 
      STR: copyMe.saving_throws.STR.clone(), 
      DEX: copyMe.saving_throws.STR.clone(),
      CON: copyMe.saving_throws.STR.clone(),
      INT: copyMe.saving_throws.STR.clone(),
      WIS: copyMe.saving_throws.STR.clone(),
      CHA: copyMe.saving_throws.STR.clone(),
    };

    this.skill_checks = copyMe.skill_checks.map(o => { return { skill_id: o.skill_id, skill_check: o.skill_check.clone() }});

    // this.armor_worn = copyMe.armor_worn;
    // this.shield_carried = copyMe.shield_carried;

    this.senses = copyMe.senses.map(o => o.clone());

    this.advantages = [...copyMe.advantages];
    this.damage_multipliers = [...copyMe.damage_multipliers];
    this.extra_attacks = copyMe.extra_attacks;
    this.unarmed_strike_scores = [...copyMe.unarmed_strike_scores];
    this.unarmed_strike_bonus_action = copyMe.unarmed_strike_bonus_action;
    this.unarmed_strike_damage_types = [...copyMe.unarmed_strike_damage_types];
    this.unarmed_strike_size = copyMe.unarmed_strike_size;
    this.unarmed_strike_count = copyMe.unarmed_strike_count;
    this.unarmed_strike_add_attack_to_dmg = copyMe.unarmed_strike_add_attack_to_dmg;
    this.show_extra_conditions = copyMe.show_extra_conditions;
    this.actions = [...copyMe.actions];
  }
  
  changed() {
    this.subscription_holder.characterChanged();
  }
}

// export class AdaptationInstance {
//   true_id: string = "";
//   cf_id: string = "";
//   transform_upgrade_feature_id: string = "";
//   transform: TransformUpgradeFeature | null = null;
//   adaptation_id: string = "";
//   adaptation: Adaptation | null = null;

//   constructor(obj?: any) {
//     this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
//     if (obj) {
//       this.cf_id = obj.cf_id ? obj.cf_id : "";
//       this.transform_upgrade_feature_id = obj.transform_upgrade_feature_id ? obj.transform_upgrade_feature_id : "";
//       this.adaptation_id = obj.adaptation_id ? obj.adaptation_id : "";
//     }
//   }

//   get name() {
//     return this.adaptation ? this.adaptation.name : "";
//   }

//   get description() {
//     return this.adaptation ? this.adaptation.description : "";
//   }

//   toDBObj(): any {
//     return {
//       true_id: this.true_id,
//       cf_id: this.cf_id,
//       transform_upgrade_feature_id: this.transform_upgrade_feature_id,
//       adaptation_id: this.adaptation_id
//     };
//   }

//   clone(): AdaptationInstance {
//     const clone = new AdaptationInstance();
//     clone.copy(this);
//     return clone;
//   }

//   copy(copyMe: AdaptationInstance): void {
//     this.true_id = copyMe.true_id;
//     this.cf_id = copyMe.cf_id;
//     this.transform_upgrade_feature_id = copyMe.transform_upgrade_feature_id;
//     this.transform = copyMe.transform;
//     this.adaptation_id = copyMe.adaptation_id;
//     this.adaptation = copyMe.adaptation;
//   }
// }
